import React, { useEffect, useMemo } from 'react'
import { useTheme, Autocomplete, TextField } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { TermHeaderContainer } from './styles'
import type { ITerm } from '../../models/Term'
import type { IDistributionSeries } from '../../models/DistributionSeries'
import GenericButton from '../GenericButton'
import { ButtonSize, ButtonType } from '../GenericButton/styles'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

export interface TermHeaderProps {
  showSeries?: boolean
  onClick?: () => void
  children?: React.ReactNode
  distributionSeries?: IDistributionSeries[] | undefined
  terms?: ITerm[] | undefined
  seriesLoading?: boolean
  termsLoading?: boolean
}

const autocompleteProps = {
  autoHighlight: true,
  disableClearable: true,
  size: 'small' as const
}

const TermHeader: React.FC<TermHeaderProps> = ({
  showSeries = true,
  onClick,
  distributionSeries = [],
  terms = [],
  seriesLoading = false,
  termsLoading = false,
  children = null
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const theme = useTheme()

  const termId = searchParams.get('termId')
  const seriesId = searchParams.get('seriesId')

  const autocompleteSx = useMemo(() => ({
    [theme.breakpoints.up('md')]: { maxWidth: '256px' }
  }), [theme])

  const handleSelectionChange = (key: 'seriesId' | 'termId') => (
    _event: React.SyntheticEvent,
    newValue: IDistributionSeries | ITerm | null
  ) => {
    const params = new URLSearchParams(searchParams)
    if (newValue) {
      params.set(key, key === 'seriesId' ? (newValue as IDistributionSeries).distributionSeriesId : (newValue as ITerm).termId)
      if (key === 'seriesId') params.delete('termId')
    } else {
      params.delete(key)
    }
    setSearchParams(params)
  }

  const selectedSeries = distributionSeries.find((series: IDistributionSeries) => series.distributionSeriesId === seriesId) ?? null
  const selectedTerm = terms.find((term: ITerm) => term.termId === termId) ?? null

  useEffect(() => {
    if (showSeries && !seriesId && distributionSeries?.length > 0) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev)
        newParams.set('seriesId', distributionSeries[0].distributionSeriesId)
        return newParams
      })
    }
  }, [distributionSeries, seriesId, showSeries, setSearchParams])

  useEffect(() => {
    if (!termId && terms?.length > 0) {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev)
        newParams.set('termId', terms[0].termId)
        return newParams
      })
    }
  }, [terms, termId, setSearchParams])

  return (
    <TermHeaderContainer hasButton={!!onClick}>
      {showSeries && (
        <Autocomplete
          id="series-select"
          {...autocompleteProps}
          value={selectedSeries}
          options={distributionSeries}
          getOptionLabel={(series) => series.title}
          renderInput={(params) => <TextField {...params} label="Survey Project" />}
          isOptionEqualToValue={(option, value) =>
            option.distributionSeriesId === value.distributionSeriesId
          }
          onChange={handleSelectionChange('seriesId')}
          fullWidth
          sx={autocompleteSx}
          loading={seriesLoading}
        />
      )}

      <Autocomplete
        id="term-select"
        {...autocompleteProps}
        value={selectedTerm}
        options={terms}
        getOptionLabel={(term) => term.termName}
        renderInput={(params) => <TextField {...params} label="Term" />}
        isOptionEqualToValue={(option, value) => option.termId === value.termId}
        onChange={handleSelectionChange('termId')}
        fullWidth
        sx={autocompleteSx}
        loading={termsLoading}
      />

      {children}

      {onClick && (
        <GenericButton
          buttonType={ButtonType.Primary}
          buttonSize={ButtonSize.Medium}
          icon={<ArrowForwardIcon />}
          onClick={onClick}
          disabled={termsLoading}
        />
      )}
    </TermHeaderContainer>
  )
}

export default TermHeader
